import checkIsAdultPassengerType from '../../../../helpers/checkIsAdultPassengerType';
import {
  baggageAllowanceEquals,
  baggageVariesByTraveler,
  isNoBagsIncluded,
} from '../../../../helpers/formatBaggageForPassenger';
import BaggageAllowanceLabel, {
  BaggageAllowanceList,
  BaggageByFlightLabel,
} from './BaggageAllowanceLabel';

const infosWithBaggageOf = (selectedFare) =>
  (selectedFare?.farePassengerInfo || []).filter(
    (p) => p.carryOnBags != null || p.checkedBags != null,
  );

const primaryInfoOf = (selectedFare) => {
  const infos = infosWithBaggageOf(selectedFare);
  if (infos.length === 0) return null;
  return infos.find((p) => checkIsAdultPassengerType(p)) || infos[0];
};

const routeOf = (origin, destination) =>
  origin && destination ? `${origin}→${destination}` : null;

/**
 * Describe the baggage of one segment (leg) across ALL of its flights. Each flight can carry
 * its own selected fare and therefore its own allowance (e.g. a connection on a different
 * cabin), so reading only flights[0] would hide that. Returns null when no flight has baggage.
 */
const describeSegment = (segment, selectFare) => {
  const flights = (segment.flights || [])
    .map((f) => {
      const fare = selectFare(f);
      const primary = primaryInfoOf(fare);
      return primary
        ? {
            primary,
            passengerInfos: infosWithBaggageOf(fare),
            routeLabel: routeOf(f.originAirportCode, f.destinationAirportCode),
          }
        : null;
    })
    .filter(Boolean);
  if (flights.length === 0) return null;

  const flightsDiffer = flights.some(
    (f) =>
      !baggageAllowanceEquals(f.primary.carryOnBags, flights[0].primary.carryOnBags) ||
      !baggageAllowanceEquals(f.primary.checkedBags, flights[0].primary.checkedBags),
  );

  return {
    flights,
    flightsDiffer,
    // Representative allowance for the segment (used when all its flights match, and to
    // compare this segment against the others).
    primary: flights[0].primary,
    passengerInfos: flights[0].passengerInfos,
    routeLabel: routeOf(segment.originAirportCode, segment.destinationAirportCode),
  };
};

/**
 * Render ONE segment's baggage as a node: the per-flight label when its flights differ,
 * otherwise the per-type labels (carry-on + checked) with the per-traveler tooltip. Optionally
 * route-prefixed (e.g. "MEX→FRA:") when shown among multiple legs.
 */
const renderSegmentBaggage = (d, { routeLabel = null, className, showInfoIndicator, key }) =>
  d.flightsDiffer ? (
    <BaggageByFlightLabel
      key={key}
      routeLabel={routeLabel}
      flights={d.flights.map((f) => ({
        routeLabel: f.routeLabel,
        carryOnBags: f.primary.carryOnBags,
        checkedBags: f.primary.checkedBags,
        passengerInfos: f.passengerInfos,
      }))}
      className={className}
      showInfoIndicator={showInfoIndicator}
    />
  ) : (
    <span key={key} className={className}>
      <BaggageAllowanceList
        allowances={[
          {
            carryOnBags: d.primary.carryOnBags,
            checkedBags: d.primary.checkedBags,
            passengerInfos: d.passengerInfos,
            routeLabel,
          },
        ]}
        className={className}
        showInfoIndicator={showInfoIndicator}
      />
    </span>
  );

/**
 * Build the baggage labels for an itinerary's fare.
 *
 * @param {object} itinerary
 * @param {object}  [options]
 * @param {number}  [options.fareIndex]         When set, reads `allFares[fareIndex]` of each
 *                                              segment (the "More Classes" rows) instead of the
 *                                              segment's `selectedFare` (the result card).
 * @param {string}  [options.className]         className applied to every label/span.
 * @param {boolean|string} [options.showInfoIndicator] Indicator style passed to the labels
 *                                              (`true` on the card, `'icon-only'` in the menu).
 * @returns {{ items: React.ReactNode[] }}
 */
const getBaggageInfo = (
  itinerary,
  { fareIndex = null, className = 'text-gray-700', showInfoIndicator = true } = {},
) => {
  // Each leg can carry a different baggage allowance (e.g. Economy outbound + Premium Economy
  // inbound), and the flights WITHIN a leg can also differ. The card shows brand/cabin for ALL
  // legs combined, so baggage must too — reading only flightSegments[0].flights[0] would hide
  // both a different inbound allowance and a different connecting-flight allowance.

  const selectFare = (f) => (fareIndex == null ? f.selectedFare : f.allFares?.[fareIndex]);

  const segments = (itinerary.flightSegments || [])
    .map((s) => describeSegment(s, selectFare))
    .filter(Boolean);
  if (segments.length === 0) return { items: [] };

  const segmentsDiffer = segments.some(
    (d) =>
      !baggageAllowanceEquals(d.primary.carryOnBags, segments[0].primary.carryOnBags) ||
      !baggageAllowanceEquals(d.primary.checkedBags, segments[0].primary.checkedBags),
  );
  const anyFlightsDiffer = segments.some((d) => d.flightsDiffer);

  // Emit ONE item per leg when the legs differ from each other OR a leg's own flights differ —
  // so the caller separates them with the same "·" used between the other fare attributes
  // (refundability, change policy, etc.). Route-prefixed (e.g.
  // "MEX→FRA:") when there is more than one leg. A leg whose flights differ shows a
  // "Bags vary by flight" label whose tooltip breaks the allowance down per flight; an
  // internally-uniform leg keeps its per-type labels with the per-traveler tooltip.
  if (segmentsDiffer || anyFlightsDiffer) {
    const showRoute = segments.length > 1;
    return {
      items: segments.map((d, i) =>
        renderSegmentBaggage(d, {
          routeLabel: showRoute ? d.routeLabel : null,
          className,
          showInfoIndicator,
          key: `bags-${i.toString()}`,
        }),
      ),
    };
  }

  // All legs identical and internally uniform — render once from the primary (adult) passenger.
  // Each label switches to the per-traveler tooltip when its own bag type differs.
  const infosWithBaggage = segments[0].passengerInfos;
  const { carryOnBags, checkedBags } = segments[0].primary;

  if (isNoBagsIncluded(carryOnBags, checkedBags) && !baggageVariesByTraveler(infosWithBaggage)) {
    return {
      items: [
        <span key="bags" className={className}>
          No bags included
        </span>,
      ],
    };
  }

  const items = [];
  if (carryOnBags != null) {
    items.push(
      <BaggageAllowanceLabel
        key="carryOn"
        allowance={carryOnBags}
        typeLabel="carry-on"
        bagKey="carryOnBags"
        passengerInfos={infosWithBaggage}
        className={className}
        showInfoIndicator={showInfoIndicator}
      />,
    );
  }
  if (checkedBags != null) {
    items.push(
      <BaggageAllowanceLabel
        key="checked"
        allowance={checkedBags}
        typeLabel="checked"
        bagKey="checkedBags"
        passengerInfos={infosWithBaggage}
        className={className}
        showInfoIndicator={showInfoIndicator}
      />,
    );
  }
  return { items };
};

/**
 * Baggage for ONE segment as a single node — same per-flight / per-traveler handling as the
 * itinerary-level builder, for callers that already iterate segments (e.g. trip configuration
 * FlightInfo). Returns null when the segment has no baggage info.
 */
export const getSegmentBaggageInfo = (
  segment,
  { fareIndex = null, className = 'text-gray-700', showInfoIndicator = true } = {},
) => {
  const selectFare = (f) => (fareIndex == null ? f.selectedFare : f.allFares?.[fareIndex]);
  const d = describeSegment(segment, selectFare);
  if (!d) return null;
  return renderSegmentBaggage(d, { routeLabel: null, className, showInfoIndicator, key: 'bags' });
};

export default getBaggageInfo;
